import React, { useState } from "react";
import { Bet } from "@/api/betting";
import { formatDate } from "../utils/formatHelpers";
import { getStatusBadge, getValidationBadge } from "../StatusBadges";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ExternalLink,
  BookOpen,
  Calendar,
  MapPin,
  Trophy,
  Tag,
  CheckCircle,
  FileJson,
  AlertTriangle,
  Info,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
} from "lucide-react";
import { PersonaAvatar } from "@/components/PersonaAvatar";
import { cn } from "@/lib/utils";
import type { Persona } from "@/api/personas";

interface SourceType {
  name: string;
  url?: string;
  publish_date?: string;
  key_information?: string;
  title?: string;
}

interface BetDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bet: Bet;
  persona?: Persona;
  onMarkAsWon?: () => void;
  onMarkAsLost?: () => void;
}

const BetDetailsModal: React.FC<BetDetailsModalProps> = ({
  isOpen,
  onClose,
  bet,
  persona,
  onMarkAsWon,
  onMarkAsLost,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const personaName = persona ? persona.name : "Unknown Agent";
  const isValidated = bet.validation_status === "validated";
  const isValidBet = bet.validation_result?.is_valid === true;

  // Extract enhanced information from validation
  const validationData = bet.validation_result;
  const betUpdates = validationData?.bet_updates;
  const eventInfo = validationData?.event_info;
  const outcome = validationData?.outcome;
  const sources = (validationData?.sources ?? []) as SourceType[];

  // Get enhanced data
  const enhancedGame = betUpdates?.game || bet.game;
  const enhancedSport = betUpdates?.sport || bet.sport;
  const enhancedPrediction = betUpdates?.prediction || bet.prediction;
  const enhancedOdds = betUpdates?.odds || bet.odds;

  // Event details
  const eventDate = eventInfo?.event_date;
  const eventStatus = eventInfo?.event_status;
  const venue = eventInfo?.venue;
  const league = eventInfo?.league;
  const teams = eventInfo?.teams || [];

  // Result info
  const resultText = bet.result?.result_text || "";
  const outcomeResult = outcome?.result;
  const outcomeScore = outcome?.score;
  const outcomeExplanation = outcome?.explanation;

  // Determine if bet is future event (pending)
  const isPendingEvent =
    eventStatus === "scheduled" || outcomeResult === "pending";

  // Check if there's real validation data
  const hasValidationData =
    isValidated &&
    (!!eventDate || !!venue || !!league || teams.length > 0 || !!outcomeScore);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80dvw] max-h-[90dvh] overflow-hidden p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl flex items-center gap-3 font-bold">
              Bet Details
              {isValidated && isValidBet && (
                <Badge className="bg-green-600/20 text-green-500 border-green-800 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" />
                  Verified
                </Badge>
              )}
              {bet.status === "won" && (
                <Badge className="bg-green-600/20 text-green-500 border-green-800">
                  Won
                </Badge>
              )}
              {bet.status === "lost" && (
                <Badge className="bg-red-600/20 text-red-500 border-red-800">
                  Lost
                </Badge>
              )}
            </DialogTitle>

            <div className="flex items-center gap-2">
              {persona && (
                <div className="flex items-center gap-2 bg-black/20 px-3 py-1.5 rounded-full">
                  <PersonaAvatar persona={persona} className="h-5 w-5" />
                  <span className="text-sm font-medium">{personaName}</span>
                </div>
              )}
              <div className="text-xs text-zinc-400">
                {formatDate(bet.created_at, true)}
              </div>
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col h-full"
        >
          <div className="border-b border-zinc-800 px-6">
            <TabsList className="bg-transparent h-12">
              <TabsTrigger
                value="details"
                className="data-[state=active]:bg-zinc-800/30"
              >
                Bet Details
              </TabsTrigger>
              {hasValidationData && (
                <TabsTrigger
                  value="validation"
                  className="data-[state=active]:bg-zinc-800/30"
                >
                  Validation
                </TabsTrigger>
              )}
              {sources.length > 0 && (
                <TabsTrigger
                  value="sources"
                  className="data-[state=active]:bg-zinc-800/30"
                >
                  Sources
                </TabsTrigger>
              )}
              <TabsTrigger
                value="raw"
                className="data-[state=active]:bg-zinc-800/30"
              >
                Raw Data
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-[calc(90dvh-180px)] w-full">
              <TabsContent value="details" className="m-0 p-0 h-full">
                <div className="p-6 space-y-8">
                  {/* Main Bet Information Card */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Event Information */}
                    <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                      <div className="bg-black/30 px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <Trophy className="h-5 w-5 text-blue-400" />
                          Event Information
                        </h3>
                        {league && (
                          <Badge className="bg-blue-950/30 border-blue-800 text-blue-400">
                            {league}
                          </Badge>
                        )}
                      </div>
                      <div className="p-4 space-y-6">
                        <div>
                          <div className="text-sm text-zinc-400 mb-2">
                            Sport
                          </div>
                          <div className="text-xl font-medium">
                            {enhancedSport}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-zinc-400 mb-2">
                            Event
                          </div>
                          <div className="text-xl font-medium">
                            {enhancedGame}
                          </div>

                          {/* Team Cards */}
                          {teams.length > 0 && (
                            <div className="mt-3 grid grid-cols-2 gap-2">
                              {teams.map((team, index) => (
                                <div
                                  key={index}
                                  className="bg-black/30 border border-zinc-800 rounded-lg p-3 text-center"
                                >
                                  <div className="font-medium">{team}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {eventDate && (
                            <div>
                              <div className="text-sm text-zinc-400 mb-2 flex items-center gap-1.5">
                                <Calendar className="h-4 w-4 text-zinc-500" />
                                <span>Event Date</span>
                              </div>
                              <div className="text-base font-medium">
                                {eventDate}
                              </div>
                            </div>
                          )}

                          {venue && (
                            <div>
                              <div className="text-sm text-zinc-400 mb-2 flex items-center gap-1.5">
                                <MapPin className="h-4 w-4 text-zinc-500" />
                                <span>Venue</span>
                              </div>
                              <div className="text-base font-medium">
                                {venue}
                              </div>
                            </div>
                          )}
                        </div>

                        {eventStatus && (
                          <div className="mt-4">
                            <Badge
                              className={cn(
                                "px-3 py-1",
                                eventStatus === "completed"
                                  ? "bg-green-900/30 text-green-500 border-green-800"
                                  : eventStatus === "scheduled"
                                    ? "bg-blue-900/30 text-blue-400 border-blue-800"
                                    : "bg-amber-900/30 text-amber-400 border-amber-800",
                              )}
                            >
                              {eventStatus === "completed" ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : eventStatus === "scheduled" ? (
                                <Clock className="h-3 w-3 mr-1" />
                              ) : (
                                <AlertTriangle className="h-3 w-3 mr-1" />
                              )}
                              <span className="capitalize">{eventStatus}</span>
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bet Details */}
                    <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                      <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <Tag className="h-5 w-5 text-green-400" />
                          Bet Details
                        </h3>
                      </div>
                      <div className="p-4 space-y-6">
                        <div>
                          <div className="text-sm text-zinc-400 mb-2">
                            Prediction
                          </div>
                          <div className="text-xl font-medium">
                            {enhancedPrediction}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-zinc-400 mb-2">Odds</div>
                          <div className="text-xl font-medium">
                            {enhancedOdds}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-zinc-400 mb-2">
                            Status
                          </div>
                          <div className="flex gap-2">
                            {getStatusBadge(bet.status)}
                            {getValidationBadge(bet.validation_status)}
                          </div>
                        </div>

                        {/* Result Information */}
                        {(bet.status !== "pending" ||
                          outcomeResult === "win" ||
                          outcomeResult === "loss") && (
                          <div>
                            <div className="text-sm text-zinc-400 mb-2">
                              Result
                            </div>
                            <div className="bg-black/30 p-3 rounded-lg border border-zinc-800">
                              {resultText || outcomeScore || (
                                <span className="text-zinc-500 italic">
                                  No result details available
                                </span>
                              )}
                            </div>
                            {outcomeExplanation && (
                              <div className="mt-2 text-sm text-zinc-400">
                                {outcomeExplanation}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pending Notice */}
                        {isPendingEvent && (
                          <div className="bg-blue-950/20 border border-blue-900 rounded-lg p-3 flex items-start gap-3">
                            <Info className="h-5 w-5 text-blue-400 mt-0.5" />
                            <div>
                              <h4 className="font-medium text-blue-400">
                                Upcoming Event
                              </h4>
                              <p className="text-sm text-zinc-300 mt-1">
                                This event is scheduled for the future. The
                                outcome will be determined after the event takes
                                place.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Original Message */}
                  {bet.raw_text && (
                    <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                      <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-purple-400" />
                          Original Message
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="bg-black/30 p-4 rounded-lg border border-zinc-800 text-zinc-300">
                          "{bet.raw_text}"
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {hasValidationData && (
                <TabsContent value="validation" className="m-0 p-0">
                  <div className="p-6 space-y-6">
                    {/* Validation Status Summary */}
                    <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                      <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          Validation Overview
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-black/30 rounded-lg border border-zinc-800 p-4">
                            <div className="text-sm text-zinc-400 mb-1">
                              Valid Bet
                            </div>
                            <div className="flex items-center gap-2">
                              {isValidBet ? (
                                <>
                                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                                  <span className="text-lg font-medium text-green-500">
                                    Verified
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-5 w-5 text-red-500" />
                                  <span className="text-lg font-medium text-red-500">
                                    Not Verified
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="bg-black/30 rounded-lg border border-zinc-800 p-4">
                            <div className="text-sm text-zinc-400 mb-1">
                              Source Count
                            </div>
                            <div className="text-lg font-medium">
                              {sources.length}{" "}
                              {sources.length === 1 ? "source" : "sources"} used
                            </div>
                          </div>

                          <div className="bg-black/30 rounded-lg border border-zinc-800 p-4">
                            <div className="text-sm text-zinc-400 mb-1">
                              Event Status
                            </div>
                            <div className="text-lg font-medium capitalize">
                              {eventStatus || "Unknown"}
                            </div>
                          </div>
                        </div>

                        {/* Event Timeline */}
                        <div className="mt-6 relative">
                          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-zinc-800"></div>

                          <div className="space-y-4">
                            <div className="flex items-start ml-4 pl-6 relative">
                              <div className="absolute left-0 transform -translate-x-4 w-2 h-2 rounded-full bg-blue-500 mt-1.5 ring-4 ring-black"></div>
                              <div className="bg-black/30 rounded-lg border border-zinc-800 p-3 w-full">
                                <div className="text-sm font-medium text-blue-400">
                                  Bet Placed
                                </div>
                                <div className="text-xs text-zinc-400 mt-0.5">
                                  {formatDate(bet.created_at, true)}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start ml-4 pl-6 relative">
                              <div className="absolute left-0 transform -translate-x-4 w-2 h-2 rounded-full bg-green-500 mt-1.5 ring-4 ring-black"></div>
                              <div className="bg-black/30 rounded-lg border border-zinc-800 p-3 w-full">
                                <div className="text-sm font-medium text-green-400">
                                  Event Scheduled
                                </div>
                                <div className="text-xs text-zinc-400 mt-0.5">
                                  {eventDate || "Unknown date"}
                                </div>
                                {venue && (
                                  <div className="text-xs text-zinc-400 mt-0.5">
                                    at {venue}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Show outcome step if the event is completed */}
                            {eventStatus === "completed" && (
                              <div className="flex items-start ml-4 pl-6 relative">
                                <div className="absolute left-0 transform -translate-x-4 w-2 h-2 rounded-full bg-purple-500 mt-1.5 ring-4 ring-black"></div>
                                <div className="bg-black/30 rounded-lg border border-zinc-800 p-3 w-full">
                                  <div className="text-sm font-medium text-purple-400">
                                    Event Completed
                                  </div>
                                  <div className="text-xs text-zinc-400 mt-0.5">
                                    {outcomeScore ? (
                                      <span>Final Score: {outcomeScore}</span>
                                    ) : (
                                      <span>Result: {bet.status}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Validation step */}
                            <div className="flex items-start ml-4 pl-6 relative">
                              <div className="absolute left-0 transform -translate-x-4 w-2 h-2 rounded-full bg-amber-500 mt-1.5 ring-4 ring-black"></div>
                              <div className="bg-black/30 rounded-lg border border-zinc-800 p-3 w-full">
                                <div className="text-sm font-medium text-amber-400">
                                  Validation Complete
                                </div>
                                <div className="text-xs text-zinc-400 mt-0.5">
                                  {isValidBet ? (
                                    <span className="text-green-400">
                                      Bet validated successfully
                                    </span>
                                  ) : (
                                    <span className="text-red-400">
                                      Validation failed
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Information from Validation */}
                    {Object.keys(betUpdates ?? {}).length > 0 && (
                      <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                        <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            <Info className="h-5 w-5 text-blue-400" />
                            Enhanced Information
                          </h3>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(betUpdates ?? {}).map(
                              ([key, value]) => (
                                <div
                                  key={key}
                                  className="bg-black/30 rounded-lg border border-zinc-800 p-3"
                                >
                                  <div className="text-sm text-zinc-400 mb-1 capitalize">
                                    {key}
                                  </div>
                                  <div className="text-base font-medium">
                                    {String(value)}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Outcome Details */}
                    {Object.keys(outcome ?? {}).length > 0 && (
                      <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                        <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            <Trophy className="h-5 w-5 text-yellow-400" />
                            Outcome Details
                          </h3>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {outcomeResult && (
                              <div className="bg-black/30 rounded-lg border border-zinc-800 p-3">
                                <div className="text-sm text-zinc-400 mb-1">
                                  Result
                                </div>
                                <div className="text-base font-medium capitalize">
                                  {outcomeResult}
                                </div>
                              </div>
                            )}

                            {outcomeScore && (
                              <div className="bg-black/30 rounded-lg border border-zinc-800 p-3">
                                <div className="text-sm text-zinc-400 mb-1">
                                  Score
                                </div>
                                <div className="text-base font-medium">
                                  {outcomeScore}
                                </div>
                              </div>
                            )}
                          </div>

                          {outcomeExplanation && (
                            <div className="mt-4 bg-black/30 rounded-lg border border-zinc-800 p-3">
                              <div className="text-sm text-zinc-400 mb-1">
                                Explanation
                              </div>
                              <div className="text-base">
                                {outcomeExplanation}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}

              {sources.length > 0 && (
                <TabsContent value="sources" className="m-0 p-0">
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {sources.map((source, index) => (
                        <div
                          key={index}
                          className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden"
                        >
                          <div className="bg-black/30 px-4 py-3 border-b border-zinc-800 flex justify-between items-center">
                            <h4 className="font-medium flex items-center gap-2">
                              <span>Source {index + 1}</span>
                              {source.url && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <a
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                      >
                                        <ExternalLink className="h-4 w-4" />
                                      </a>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Visit source website</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </h4>
                            {source.publish_date && (
                              <Badge variant="outline" className="text-xs">
                                Published: {source.publish_date}
                              </Badge>
                            )}
                          </div>
                          <div className="p-4 space-y-3">
                            <div className="text-lg font-medium">
                              {source.name || source.title || "Unnamed Source"}
                            </div>

                            {source.key_information && (
                              <div className="mt-2">
                                <div className="text-sm text-zinc-400 mb-1 flex items-center gap-1">
                                  <FileText className="h-4 w-4" />
                                  <span>Key Information</span>
                                </div>
                                <div className="bg-black/30 p-3 rounded-lg border border-zinc-800 text-sm">
                                  {source.key_information}
                                </div>
                              </div>
                            )}

                            {source.url && (
                              <div className="mt-1 flex">
                                <a
                                  href={source.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-auto"
                                >
                                  <span>Visit source</span>
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Sources Verification Summary */}
                    <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                      <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          Verification Summary
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-12 w-12 bg-green-600/20 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="h-6 w-6 text-green-500" />
                          </div>
                          <div>
                            <div className="text-lg font-medium">
                              Verified with {sources.length}{" "}
                              {sources.length === 1 ? "source" : "sources"}
                            </div>
                            <div className="text-sm text-zinc-400">
                              Betting information has been cross-referenced with
                              trusted sources
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="text-sm text-zinc-400 mb-2">
                            Verification Confidence
                          </div>
                          <div className="relative">
                            <Progress
                              value={
                                sources.length >= 3 ? 100 : sources.length * 33
                              }
                              className="h-3 bg-zinc-800"
                            />
                            <div className="flex justify-between text-xs text-zinc-500 mt-1">
                              <span>Low</span>
                              <span>Medium</span>
                              <span>High</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="raw" className="m-0 p-0">
                <div className="p-6">
                  <div className="bg-black/30 rounded-xl border border-zinc-800 overflow-hidden">
                    <div className="bg-black/30 px-4 py-3 border-b border-zinc-800">
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <FileJson className="h-5 w-5 text-zinc-400" />
                        Raw Data
                      </h3>
                    </div>
                    <div className="p-4">
                      <Accordion
                        type="single"
                        collapsible
                        className="w-full"
                        defaultValue="validation"
                      >
                        <AccordionItem
                          value="validation"
                          className="border-zinc-800"
                        >
                          <AccordionTrigger className="hover:no-underline py-3">
                            <span className="text-zinc-200 font-medium">
                              Validation Data
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <pre className="bg-black/40 p-4 rounded-lg text-xs overflow-auto max-h-96 border border-zinc-800">
                              {JSON.stringify(bet.validation_result, null, 2) ||
                                "No validation data available"}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem
                          value="metadata"
                          className="border-zinc-800"
                        >
                          <AccordionTrigger className="hover:no-underline py-3">
                            <span className="text-zinc-200 font-medium">
                              Metadata
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <pre className="bg-black/40 p-4 rounded-lg text-xs overflow-auto max-h-96 border border-zinc-800">
                              {JSON.stringify(bet.metadata, null, 2) ||
                                "No metadata available"}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem
                          value="result"
                          className="border-zinc-800"
                        >
                          <AccordionTrigger className="hover:no-underline py-3">
                            <span className="text-zinc-200 font-medium">
                              Result Data
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <pre className="bg-black/40 p-4 rounded-lg text-xs overflow-auto max-h-96 border border-zinc-800">
                              {JSON.stringify(bet.result, null, 2) ||
                                "No result data available"}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem
                          value="complete"
                          className="border-zinc-800"
                        >
                          <AccordionTrigger className="hover:no-underline py-3">
                            <span className="text-zinc-200 font-medium">
                              Complete Bet Object
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <pre className="bg-black/40 p-4 rounded-lg text-xs overflow-auto max-h-96 border border-zinc-800">
                              {JSON.stringify(bet, null, 2)}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </div>
        </Tabs>

        <DialogFooter className="p-4 border-t border-zinc-800">
          {bet.status === "pending" && onMarkAsWon && onMarkAsLost && (
            <>
              <Button
                variant="outline"
                className="bg-green-600/20 hover:bg-green-600/30 text-green-500 border-green-800"
                onClick={onMarkAsWon}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark as Won
              </Button>
              <Button
                variant="outline"
                className="bg-red-600/20 hover:bg-red-600/30 text-red-500 border-red-800"
                onClick={onMarkAsLost}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Mark as Lost
              </Button>
            </>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BetDetailsModal;
